import clsx from "clsx";
import { useEffect, useMemo, useRef } from "react";
import { isFileTypeVideo, isMediaOfTypeVideo } from "utils/helpers";
import useBlurHashEncoder from "components/hooks/useBlurHashEncoder";
import ErrorMessage from "./forms/ErrorMessage";
import { CloseIcon, LoadingIcon, UploadImageIcon } from "./Icon";
import VideoPreviewer from "./VideoPreviewer";

const acceptedImageFormats = "image/*";
const acceptedVideoFormats = ",video/mp4";

export default function BigImageUpload({
  acceptVideo,
  existingImageURL,
  choosenFile,
  id,
  errors,
  icon,
  labelClasses,
  isDisabled,
  label,
  subLabel,
  setFieldValue,
  handleClearImage,
  setGeneratedHash,
}: {
  acceptVideo?: boolean;
  choosenFile?: File;
  id: string;
  errors?: string | undefined;
  icon?: JSX.Element;
  label?: string;
  labelClasses?: string;
  isDisabled?: boolean;
  subLabel?: string;
  existingImageURL?: string;
  setFieldValue: (file: FileList) => void;
  handleClearImage: () => void;
  setGeneratedHash?: (hash: string) => void;
}) {
  const ref = useRef<HTMLLabelElement | undefined>();
  const inputRef = useRef<HTMLInputElement>(null);
  function handleSetImage(e: any) {
    // This is needed as by default
    if (e.target.files.length) {
      setFieldValue(e.target.files);
    }
  }

  const renderClearImageButton = choosenFile && !isDisabled;
  const isFileVideo = choosenFile
    ? isFileTypeVideo(choosenFile)
    : existingImageURL && isMediaOfTypeVideo(existingImageURL);

  const fileSrc = useMemo(
    () =>
      // @ts-ignore
      choosenFile ? URL.createObjectURL(choosenFile) : existingImageURL,
    [choosenFile],
  );

  const handleVideoClick = () => {
    ref.current?.click();
  };

  // Gerenate blurhash for the image
  const [imageHash, isImageHashLoading] = useBlurHashEncoder(setGeneratedHash ? fileSrc : undefined);

  useEffect(() => {
    if (typeof imageHash === "string") setGeneratedHash?.(imageHash);
  }, [imageHash]);

  const handleClearOnClick = () => {
    if (inputRef.current?.files) {
      inputRef.current.files = null;
      inputRef.current.value = "";
    }
    handleClearImage();
  };

  return (
    <div
      className="relative mt-1 flex w-full
    flex-col justify-center"
    >
      <label
        // @ts-ignore
        ref={ref}
        htmlFor={id}
        className="flex aspect-1 w-full cursor-pointer
          justify-center rounded-xl border border-dashed border-white bg-primary
          font-medium text-white"
      >
        <div className="h-full w-full space-y-1 rounded-md text-center">
          {choosenFile || existingImageURL ? (
            <div className="h-full w-full">
              {isFileVideo ? (
                // This div makes sure that video always fits inside the parent container.
                <VideoPreviewer videoPlayerId="memento_form_video" src={fileSrc} handleVideoClick={handleVideoClick} />
              ) : (
                <img
                  className={clsx(
                    "h-full w-full rounded-xl object-cover focus:opacity-5 md:hover:opacity-50",
                    isDisabled && "opacity-30 hover:opacity-30",
                  )}
                  src={fileSrc}
                  alt="cover"
                />
              )}
              {isImageHashLoading && (
                <div className="absolute inset-0 grid  place-items-center bg-white bg-opacity-10  ">
                  <div className="mix aspect-1 w-1/5 mix-blend-exclusion">
                    <LoadingIcon />
                    <p>Generating Thumbnail...</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center font-medium">
              {icon}
              <div className="px-1 text-lg">
                <span className={labelClasses}>{label}</span>
                {subLabel && <p className="text-sm text-white">{subLabel}</p>}
              </div>
            </div>
          )}
        </div>
      </label>
      {renderClearImageButton && (
        <button type="button" className="absolute -top-2 right-0 p-2" onClick={handleClearOnClick}>
          <CloseIcon className="h-4 w-4 text-white mix-blend-difference" />
        </button>
      )}

      <input
        disabled={isDisabled}
        id={id}
        accept={clsx(acceptedImageFormats, acceptVideo && acceptedVideoFormats)}
        name={id}
        type="file"
        className="sr-only"
        onChange={handleSetImage}
        ref={inputRef}
      />
      <div className="mb-2 mt-1 min-h-[18px] text-center">{errors && <ErrorMessage errorMessage={errors} />}</div>
    </div>
  );
}

BigImageUpload.defaultProps = {
  icon: <UploadImageIcon />,
  labelClasses: "",
  label: "Click to Select a File",
};
